import { config } from '@/config/environment';
import { ApiResponse, PaginatedResponse } from '@/types/api';
import { Lead } from '@/types/index';

// Mock data generators
const generateMockLead = (id: number): Lead => {
  const riskScore = Math.floor(Math.random() * 100);
  const statuses = ['pending', 'approved', 'blocked'] as const;
  const sources = ['Website', 'Facebook', 'Google Ads', 'Email Campaign', 'Referral'];
  const names = ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Wilson', 'David Brown'];
  const domains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'example.com'];
  
  return {
    id: `lead_${id}`,
    organization_id: 'org_mock',
    external_id: null,
    source: sources[id % sources.length],
    first_name: names[id % names.length].split(' ')[0],
    last_name: names[id % names.length].split(' ')[1],
    email: `user${id}@${domains[id % domains.length]}`,
    phone: `226243317${String(id).padStart(2, '0')}`,
    ip_address: `192.168.${Math.floor(id / 255)}.${id % 255}`,
    risk_score: riskScore,
    risk_level: riskScore > 70 ? 'high' : riskScore > 40 ? 'medium' : 'low',
    status: statuses[Math.floor(Math.random() * statuses.length)],
    detected_patterns: riskScore > 70 ? ['sequential_phone', 'datacenter_ip'] : 
                      riskScore > 40 ? ['zip_concentration'] : [],
    created_at: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
    submitted_at: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString()
  };
};

// Mock API responses with wrapped format
export const mockResponses: Record<string, any> = {
  '/api/v1/leads': (params: URLSearchParams) => {
    const page = parseInt(params.get('page') || '1');
    const pageSize = parseInt(params.get('page_size') || '20');
    const total = 247;
    
    const items = Array.from({ length: Math.min(pageSize, total) }, (_, i) => 
      generateMockLead((page - 1) * pageSize + i)
    );
    
    return {
      data: {
        items,
        total,
        page,
        page_size: pageSize,
        total_pages: Math.ceil(total / pageSize)
      } as PaginatedResponse<Lead>,
      success: true,
      message: 'Leads retrieved successfully',
      metadata: {
        organization_id: 'org_mock',
        user_id: 'user_mock',
        request_id: crypto.randomUUID(),
        timestamp: new Date().toISOString(),
        processing_time_ms: 45 + Math.random() * 50
      }
    };
  },
  
  '/api/v1/fraud/detect': {
    data: {
      lead_id: crypto.randomUUID(),
      risk_score: 75,
      fraud_indicators: {
        sequential_phone: true,
        datacenter_ip: true,
        suspicious_email: false,
        zip_concentration: true,
        velocity_attack: false
      },
      patterns_detected: ['sequential_phone', 'datacenter_ip', 'zip_concentration'],
      recommendation: 'block',
      confidence: 0.89,
      processing_time_ms: 52
    },
    success: true,
    message: 'Fraud analysis complete'
  },
  
  '/api/v1/fraud/patterns': {
    data: [
      {
        pattern_type: 'sequential_phone',
        severity: 'high',
        affected_leads: 23,
        detection_time: new Date().toISOString(),
        details: {
          sequential_phones: ['2262433171', '2262433172', '2262433173']
        }
      },
      {
        pattern_type: 'datacenter_ip',
        severity: 'critical',
        affected_leads: 45,
        detection_time: new Date().toISOString(),
        details: {
          datacenter_ips: ['169.197.85.171', '207.244.89.166']
        }
      }
    ],
    success: true,
    message: 'Pattern analysis complete'
  },
  
  '/api/v1/analytics/dashboard': {
    data: {
      total_leads: 3247,
      fraud_rate: 0.32,
      blocked_leads: 1039,
      amount_saved: 31170,
      patterns_detected: {
        sequential_phone: 312,
        datacenter_ip: 892,
        zip_concentration: 432,
        suspicious_email: 234,
        velocity_attack: 89
      },
      real_time_stats: {
        leads_per_hour: 45,
        fraud_per_hour: 14,
        avg_processing_time: 52
      }
    },
    success: true,
    message: 'Dashboard metrics retrieved'
  },
  
  '/api/v1/leads/bulk/approve': {
    data: {
      success: 15,
      failed: 0,
      errors: []
    },
    success: true,
    message: 'Bulk approval completed'
  },
  
  '/api/v1/leads/bulk/reject': {
    data: {
      success: 8,
      failed: 1,
      errors: [
        { id: 'lead_123', error: 'Lead already processed' }
      ]
    },
    success: true,
    message: 'Bulk rejection completed'
  },
  
  '/api/v1/fraud/stats/realtime': {
    data: {
      leads_today: 156,
      fraud_detected: 48,
      amount_saved: 1440,
      detection_rate: 0.31,
      active_patterns: 7
    },
    success: true,
    message: 'Real-time stats retrieved'
  }
};

// Mock API interceptor for development
export function setupMockAPI() {
  if (!config.mockMode) {
    return;
  }

  console.log('🔧 Setting up mock API interceptor');
  
  // Store original fetch
  const originalFetch = window.fetch;
  
  // Override fetch for mock responses
  window.fetch = async (url: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const urlString = url.toString();
    const urlObj = new URL(urlString);
    
    // Only intercept our API calls
    if (!urlString.includes(config.apiUrl)) {
      return originalFetch(url, init);
    }
    
    const endpoint = urlObj.pathname;
    console.log(`🎭 Mock API: ${init?.method || 'GET'} ${endpoint}`);
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 200 + Math.random() * 300));
    
    // Check if we have a mock response for this endpoint
    let mockResponse = mockResponses[endpoint];
    
    // Handle dynamic responses (functions)
    if (typeof mockResponse === 'function') {
      mockResponse = mockResponse(urlObj.searchParams);
    }
    
    if (mockResponse) {
      return new Response(
        JSON.stringify(mockResponse),
        {
          status: 200,
          statusText: 'OK',
          headers: { 
            'Content-Type': 'application/json',
            'X-Mock-Response': 'true'
          }
        }
      );
    }
    
    // Handle single lead requests
    if (endpoint.match(/^\/api\/v1\/leads\/lead_\d+$/)) {
      const leadId = endpoint.split('/').pop();
      const leadNumber = parseInt(leadId?.replace('lead_', '') || '0');
      
      return new Response(
        JSON.stringify({
          data: generateMockLead(leadNumber),
          success: true,
          message: 'Lead retrieved successfully'
        }),
        {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        }
      );
    }
    
    // Handle approve/reject actions
    if (endpoint.includes('/approve') || endpoint.includes('/reject') || endpoint.includes('/review')) {
      const leadId = endpoint.split('/')[4]; // Extract lead ID
      const action = endpoint.split('/').pop();
      
      return new Response(
        JSON.stringify({
          data: {
            ...generateMockLead(parseInt(leadId?.replace('lead_', '') || '0')),
            status: action === 'approve' ? 'approved' : action === 'reject' ? 'blocked' : 'reviewing'
          },
          success: true,
          message: `Lead ${action}d successfully`
        }),
        {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        }
      );
    }
    
    // Fallback for unknown endpoints
    console.warn(`🎭 Mock API: No mock response for ${endpoint}`);
    
    return new Response(
      JSON.stringify({
        data: null,
        success: false,
        error: 'Mock endpoint not implemented',
        message: `Mock response not configured for ${endpoint}`
      }),
      {
        status: 404,
        headers: { 'Content-Type': 'application/json' }
      }
    );
  };
  
  console.log('✅ Mock API interceptor active');
}

// Auto-setup in development
if (config.mockMode && typeof window !== 'undefined') {
  setupMockAPI();
}